import dayjs from "dayjs"
import { isPromiseScheduleActive } from "../components/shared"
import { messageProblem, sortConversation } from "./conversationEditing"
import type { EvaluationCase, Scenario } from "./types"

export type ValidatedField =
  | "title"
  | "asOf"
  | "expectedTerminal"
  | "principalDue"
  | "lateChargeDue"
  | "overdueDays"
  | "payday"
  | "contactCountTotal"
  | "activePromise"
  | "promiseHistory"
  | "contactLogs"
  | "operatorMemos"
  | "contracts"
  | "conversation"

const MAX_AMOUNT = 100_000_000
const isAmount = (value: unknown) =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= MAX_AMOUNT
const isOptionalPositiveAmount = (value: number | null | undefined) =>
  value == null || (isAmount(value) && value >= 1)
const time = (iso: string) => Date.parse(iso)

/** 約束・交渉記録・メモ・契約の検証（サーバーの DunningEvaluationScenarioRecords と揃える） */
function validateRecords(scenario: Scenario): Partial<Record<ValidatedField, string>> {
  const errors: Partial<Record<ValidatedField, string>> = {}
  const asOf = time(scenario.asOf)
  const active = scenario.activePromise
  if (active) {
    // 23:59 の約束はその日の 23:59:59 まで有効（サーバ・時点の組み立て直しと同じ DunningPaymentPromise.active_at? の規則）
    if (
      Number.isNaN(time(active.scheduledOn)) ||
      !isPromiseScheduleActive(dayjs(active.scheduledOn), dayjs(asOf))
    )
      errors.activePromise =
        "有効な約束の期日は基準日時以降にしてください（過ぎた約束は約束履歴に入れてください）。"
    else if (!isOptionalPositiveAmount(active.amount))
      errors.activePromise = "有効な約束の金額は1円以上の整数か、空欄にしてください。"
  }
  for (const promise of scenario.promiseHistory ?? []) {
    if (
      Number.isNaN(time(promise.promisedAt)) ||
      Number.isNaN(time(promise.scheduledOn)) ||
      !isOptionalPositiveAmount(promise.amount)
    )
      errors.promiseHistory = "約束履歴の日時・金額を確認してください。"
    else if (time(promise.promisedAt) > asOf)
      errors.promiseHistory = "約束履歴の約束した日時は基準日時以前にしてください。"
  }
  for (const log of scenario.contactLogs ?? []) {
    if (Number.isNaN(time(log.contactedAt)) || time(log.contactedAt) > asOf)
      errors.contactLogs = "交渉記録の連絡日時は基準日時以前にしてください。"
  }
  for (const memo of scenario.operatorMemos ?? []) {
    if (!memo.body.trim()) errors.operatorMemos = "メモの本文を入力してください。"
    else if (Number.isNaN(time(memo.createdAt)) || time(memo.createdAt) > asOf)
      errors.operatorMemos = "メモの記入日時は基準日時以前にしてください。"
  }
  ;(scenario.contracts ?? []).forEach((contract, index) => {
    const label = `契約${index + 1}`
    const rows = contract.installments
    if (
      rows.some(
        (row) =>
          !row.scheduledOn ||
          (row.numberOfPayment != null &&
            (!Number.isInteger(row.numberOfPayment) ||
              row.numberOfPayment < 1 ||
              row.numberOfPayment > 1000)) ||
          (row.lastPaidAt != null && Number.isNaN(time(row.lastPaidAt))) ||
          !isAmount(row.paymentTotalAmount) ||
          !isAmount(row.paidAmount) ||
          row.paidAmount > row.paymentTotalAmount
      )
    ) {
      errors.contracts = `${label}の分割予定の回（1〜1000）・約定日・金額・入金済（金額以下）・入金日を確認してください。`
      return
    }
    if (!isAmount(contract.remainingPaymentCount) || !isAmount(contract.remainingBalance)) {
      errors.contracts = `${label}の残回数・残高を入力してください。`
      return
    }
    const unpaid = rows.filter((row) => row.paidAmount < row.paymentTotalAmount)
    const outstanding = unpaid.reduce(
      (sum, row) => sum + row.paymentTotalAmount - row.paidAmount,
      0
    )
    if (contract.remainingPaymentCount < unpaid.length)
      errors.contracts = `${label}の残回数は、分割予定のうち未払いの回数（${unpaid.length}回）以上にしてください。`
    else if (contract.remainingBalance < outstanding)
      errors.contracts = `${label}の残高は、分割予定の未払い額の合計（${outstanding.toLocaleString("ja-JP")}円）以上にしてください。`
  })
  return errors
}

/** Mirror the server's numeric bounds; never send invalid edits or copies. */
export function validateEvaluationCase(
  record: EvaluationCase
): Partial<Record<ValidatedField, string>> {
  const errors: Partial<Record<ValidatedField, string>> = {}
  if (!record.title.trim()) errors.title = "ケース名を入力してください。"
  if (!record.expectedTerminal) errors.expectedTerminal = "期待する対応を選んでください。"
  // 基準日時の欄を消した途中の値（空）はサーバも「基準日時を指定してください」で弾く
  if (Number.isNaN(time(record.scenario.asOf))) errors.asOf = "基準日時を入力してください。"
  const labels = {
    principalDue: "遅延金額",
    overdueDays: "遅延日数",
  } as const
  for (const key of Object.keys(labels) as Array<keyof typeof labels>) {
    const value = record.scenario[key]
    if (!Number.isInteger(value) || value < 0 || value > 100_000_000) {
      errors[key] = `${labels[key]}は0以上100,000,000以下の整数で入力してください。`
    }
  }
  const lateChargeDue = record.scenario.lateChargeDue
  if (!Number.isInteger(lateChargeDue) || lateChargeDue < 0 || lateChargeDue > 100_000_000) {
    errors.lateChargeDue = "遅延損害金は0以上100,000,000以下の整数で指定してください。"
  }
  const payday = record.scenario.payday
  if (payday !== null && (!Number.isInteger(payday) || payday < 1 || payday > 31)) {
    errors.payday = "給料日は1〜31の整数で入力してください。未確認なら空欄にしてください。"
  }
  const count = record.scenario.contactCountTotal
  if (count != null && (!Number.isInteger(count) || count < 0 || count > 100_000)) {
    errors.contactCountTotal =
      "接触回数は0以上100,000以下の整数で指定してください。数えるなら空欄にしてください。"
  }
  const conversation = sortConversation(record.scenario.conversation)
  const problems = conversation.flatMap((message, index) => {
    const problem = messageProblem(message, record.scenario.asOf)
    return problem ? [`発言 ${index + 1}：${problem}`] : []
  })
  if (problems.length > 0)
    errors.conversation = `督促チャットを確認してください（${problems.join("、")}）。`
  return { ...errors, ...validateRecords(record.scenario) }
}
